import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getGatewayCreateCustomerDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/create-customer.form-default-values';
import { GatewayCreateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/create-customer.model';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { CreateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/customers/create-customer.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { CustomerCreateFormController } from '@app/feature/customer/customer-create/customer-create-form/customer-create-form.controller';
import { CreateCustomerContextProvider } from '@app/feature/customer/customer.context';
import { useCpmsBoCreateCustomerMutation } from '@service/cpms-bo-customer.service';

interface CustomerCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const CustomerCreateController = ({
  onCancel,
  onSuccess,
}: CustomerCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createCustomerMutation] = useCpmsBoCreateCustomerMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateCustomerModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.customer-create.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    GatewayCreateCustomerModel
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const gatewayCreateCustomer = removeEmptyFields(fieldValues);

    createCustomerMutation(gatewayCreateCustomer)
      .unwrap()
      .then(() => {
        toast.success(t('$.customer-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.customer-create.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getGatewayCreateCustomerDefaultValues();

  return (
    <CreateCustomerContextProvider data={data}>
      <CustomerCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateCustomerContextProvider>
  );
};
